import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import { downloadFileByLink } from "../api/storageApi";

type Status = "loading" | "downloading" | "success" | "error";

interface ErrorInfo {
  title: string;
  message: string;
  hint?: string;
  retryable: boolean;
  retryAfter?: number; // segundos
  variant: "warning" | "error" | "info";
}

/**
 * Ruta /file/:linkId — descarga directa.
 * - Sin sesión → /login (con redirect back).
 * - Con sesión → fetch + blob, sin nueva pestaña.
 * - UI amigable: loader, éxito, error con mensaje claro y reintento.
 */
export default function FileLink() {
  const { linkId } = useParams<{ linkId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const triggeredRef = useRef(false);

  const [status, setStatus] = useState<Status>("loading");
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [countdown, setCountdown] = useState<number>(0);

  const buildErrorInfo = (e: any): ErrorInfo => {
    const status: number | undefined = e?.status;
    const code: string | undefined = e?.code;
    const retryAfter: number | undefined = e?.retryAfter;

    if (status === 429 || code === "RATE_LIMIT_EXCEEDED") {
      return {
        title: "Demasiados intentos",
        message: "Has hecho varias peticiones seguidas. Espera unos segundos y volveremos a intentarlo.",
        retryable: true,
        retryAfter: retryAfter || 5,
        variant: "warning",
      };
    }
    if (status === 404 || code === "NOT_FOUND") {
      return {
        title: "Archivo no encontrado",
        message: "El enlace no existe o ha sido eliminado.",
        hint: "Comprueba el enlace o pide uno nuevo a quien te lo compartió.",
        retryable: false,
        variant: "error",
      };
    }
    if (status === 410 || code === "LINK_EXPIRED") {
      return {
        title: "Enlace caducado",
        message: "Este enlace ya no está disponible porque ha expirado.",
        hint: "Solicita un nuevo enlace al propietario del archivo.",
        retryable: false,
        variant: "warning",
      };
    }
    if (status === 403 || code === "FORBIDDEN") {
      return {
        title: "Sin permisos",
        message: "Tu cuenta no tiene acceso a este archivo.",
        hint: "Contacta con quien te compartió el enlace.",
        retryable: false,
        variant: "error",
      };
    }
    if (status && status >= 500) {
      return {
        title: "Error del servidor",
        message: "Algo ha fallado en nuestro lado. Inténtalo de nuevo en unos segundos.",
        retryable: true,
        variant: "error",
      };
    }
    return {
      title: "No se pudo descargar el archivo",
      message: e?.message || "Ha ocurrido un error inesperado.",
      retryable: true,
      variant: "error",
    };
  };

  const runDownload = useCallback(async () => {
    if (!linkId) return;
    setStatus("loading");
    setErrorInfo(null);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        navigate("/login", { replace: true, state: { from: location } });
        return;
      }
      setStatus("downloading");
      await downloadFileByLink(linkId);
      setStatus("success");
    } catch (e: any) {
      if (e?.status === 401) {
        navigate("/login", { replace: true, state: { from: location } });
        return;
      }
      console.error("❌ Error descargando enlace:", e);
      const info = buildErrorInfo(e);
      setErrorInfo(info);
      setStatus("error");
      if (info.retryable && info.retryAfter) setCountdown(info.retryAfter);
    }
  }, [linkId, navigate, location]);

  useEffect(() => {
    if (!linkId) {
      navigate("/", { replace: true });
      return;
    }
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    runDownload();
  }, [linkId, navigate, runDownload]);

  // Cuenta atrás + auto-retry para rate limit
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  useEffect(() => {
    if (status === "error" && errorInfo?.retryAfter && countdown === 0 && errorInfo.retryable) {
      // Auto-retry una vez expirado el rate limit
      runDownload();
    }
  }, [countdown, errorInfo, status, runDownload]);

  return (
    <div style={styles.page}>
      <style>{keyframes}</style>
      <div style={styles.card}>
        {status === "loading" && <LoadingState text="Verificando sesión…" />}
        {status === "downloading" && <LoadingState text="Preparando descarga…" />}
        {status === "success" && <SuccessState onRetry={runDownload} />}
        {status === "error" && errorInfo && (
          <ErrorState
            info={errorInfo}
            countdown={countdown}
            onRetry={runDownload}
            onHome={() => navigate("/", { replace: true })}
          />
        )}
      </div>
    </div>
  );
}

// ─── Subcomponentes ───────────────────────────────────────────────────

function LoadingState({ text }: { text: string }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={styles.spinner} />
      <div style={styles.title}>{text}</div>
      <div style={styles.subtitle}>La descarga comenzará automáticamente</div>
    </div>
  );
}

function SuccessState({ onRetry }: { onRetry: () => void }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ ...styles.iconCircle, background: "#10b981" }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <div style={styles.title}>¡Descarga iniciada!</div>
      <div style={styles.subtitle}>Revisa tu carpeta de descargas</div>
      <div style={styles.buttons}>
        <button onClick={onRetry} style={{ ...styles.btn, ...styles.btnPrimary, background: "#10b981" }}>
          Volver a descargar
        </button>
      </div>
    </div>
  );
}

function ErrorState({
  info,
  countdown,
  onRetry,
  // onHome,
}: {
  info: ErrorInfo;
  countdown: number;
  onRetry: () => void;
  onHome: () => void;
}) {
  const colors = {
    error: "#ef4444",
    warning: "#f59e0b",
    info: "#3b82f6",
  };
  const accent = colors[info.variant];
  const isWaiting = info.retryable && countdown > 0;

  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ ...styles.iconCircle, background: accent }}>
        {info.variant === "warning" ? (
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 9v4M12 17h.01" />
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          </svg>
        ) : (
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        )}
      </div>

      <div style={styles.title}>{info.title}</div>
      <div style={{ ...styles.subtitle, marginBottom: info.hint ? 8 : 24 }}>{info.message}</div>
      {info.hint && <div style={{ ...styles.hint, marginBottom: 24 }}>{info.hint}</div>}

      {isWaiting && (
        <div style={styles.countdown}>
          Reintentando en <strong style={{ color: accent }}>{countdown}s</strong>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                background: accent,
                width: `${((info.retryAfter! - countdown) / info.retryAfter!) * 100}%`,
              }}
            />
          </div>
        </div>
      )}

      <div style={styles.buttons}>
        {info.retryable && !isWaiting && (
          <button onClick={onRetry} style={{ ...styles.btn, ...styles.btnPrimary, background: accent }}>
            Reintentar
          </button>
        )}
       </div>
    </div>
  );
}

// ─── Estilos ──────────────────────────────────────────────────────────

const keyframes = `
  @keyframes spin { to { transform: rotate(360deg) } }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }
`;

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#0f1117",
    fontFamily: "'DM Sans', system-ui, sans-serif",
    padding: 24,
  },
  card: {
    width: "100%",
    maxWidth: 420,
    background: "#1a1d27",
    border: "1px solid #262a36",
    borderRadius: 16,
    padding: "40px 32px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
    color: "#e2e8f0",
    animation: "fadeIn 0.3s ease-out",
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  spinner: {
    width: 48,
    height: 48,
    margin: "0 auto 20px",
    border: "3px solid #262a36",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 8,
    color: "#f1f5f9",
  },
  subtitle: {
    fontSize: 14,
    color: "#94a3b8",
    lineHeight: 1.5,
  },
  hint: {
    fontSize: 13,
    color: "#64748b",
    fontStyle: "italic",
    lineHeight: 1.5,
  },
  countdown: {
    fontSize: 13,
    color: "#94a3b8",
    marginBottom: 24,
  },
  progressBar: {
    width: "100%",
    height: 4,
    background: "#262a36",
    borderRadius: 2,
    overflow: "hidden",
    marginTop: 10,
  },
  progressFill: {
    height: "100%",
    transition: "width 1s linear",
  },
  buttons: {
    display: "flex",
    gap: 8,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  btn: {
    padding: "10px 20px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    fontFamily: "inherit",
    transition: "opacity 0.2s",
  },
  btnPrimary: {
    color: "white",
  },
  btnSecondary: {
    background: "transparent",
    color: "#94a3b8",
    border: "1px solid #262a36",
  },
};
